import React from 'react';

import { get, isNil } from 'lodash';
import { distanceInRange, haversineDistance } from '../../../../utils/libs';

import type { JobHiringAttendance } from '../type';

export const MAX_DISTANCE = 25; // Meters

type Location = { latitude: number; longitude: number };

const getLocation = (
  object: unknown,
  latitudePath: string | string[],
  longitudePath: string | string[],
): Location | null => {
  const latitude = get(object, latitudePath);
  const longitude = get(object, longitudePath);

  if (isNil(latitude) || isNil(longitude)) {
    return null;
  }
  return { latitude, longitude };
};

export const getRestaurantLocation = (attendance: JobHiringAttendance | null | undefined) =>
  getLocation(attendance, ['jobHiring', 'restaurant', 'latitude'], ['jobHiring', 'restaurant', 'longitude']);

export const getStartOperationLocation = (attendance: JobHiringAttendance | null | undefined) =>
  getLocation(attendance, 'startOperationLatitude', 'startOperationLongitude');

export const getEndOperationLocation = (attendance: JobHiringAttendance | null | undefined) =>
  getLocation(attendance, 'endOperationLatitude', 'endOperationLongitude');

export const isAttendanceInRange = (
  attendance: JobHiringAttendance | null | undefined,
  operation: 'start' | 'end' | 'all' = 'all',
  range = MAX_DISTANCE,
) => {
  const restaurantLocation = getRestaurantLocation(attendance);
  const startOperationLocation = getStartOperationLocation(attendance);
  const endOperationLocation = getEndOperationLocation(attendance);

  const startOperationDistance = haversineDistance(restaurantLocation, startOperationLocation);
  const endOperationDistance = haversineDistance(restaurantLocation, endOperationLocation);

  switch (operation) {
    case 'start':
      return distanceInRange(startOperationDistance, range);
    case 'end':
      return distanceInRange(endOperationDistance, range);
    case 'all':
      return distanceInRange(startOperationDistance, range) && distanceInRange(endOperationDistance, range);
    default:
      return distanceInRange(startOperationDistance, range) && distanceInRange(endOperationDistance, range);
  }
};

const useAttendanceLocation = (attendance: JobHiringAttendance | null | undefined, range = MAX_DISTANCE) => {
  // Locations
  const restaurantLocation = React.useMemo(() => getRestaurantLocation(attendance), [attendance]);
  const startOperationLocation = React.useMemo(() => getStartOperationLocation(attendance), [attendance]);
  const endOperationLocation = React.useMemo(() => getEndOperationLocation(attendance), [attendance]);

  // Distances
  const startOperationDistance = React.useMemo(
    () => haversineDistance(restaurantLocation, startOperationLocation),
    [restaurantLocation, startOperationLocation],
  );
  const endOperationDistance = React.useMemo(
    () => haversineDistance(restaurantLocation, endOperationLocation),
    [restaurantLocation, endOperationLocation],
  );

  // Range checks
  const isStartOperationInRange = React.useMemo(
    () => distanceInRange(startOperationDistance, range),
    [startOperationDistance, range],
  );
  const isEndOperationInRange = React.useMemo(
    () => distanceInRange(endOperationDistance, range),
    [endOperationDistance, range],
  );

  return {
    restaurantLocation,
    startOperationLocation,
    endOperationLocation,

    startOperationDistance,
    endOperationDistance,

    isStartOperationInRange,
    isEndOperationInRange,
  };
};

export default useAttendanceLocation;
